import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MapPin, Clock, Map as MapIcon } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { catColor } from "@/lib/lore";
import { evDate } from "@/lib/eventDate";
import { fmtT } from "@/lib/calFormat";

export const TimeScrubber = ({ fmt, timelineId, min, max, value, onChange, onOpenEntity, mapLink }) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => { const t = setTimeout(() => setDebounced(value), 200); return () => clearTimeout(t); }, [value]);
  const { data } = useQuery({
    queryKey: ["snapshot", timelineId, debounced],
    queryFn: () => api.get(`/timelines/${timelineId}/snapshot`, { params: { t: debounced } }).then((r) => r.data),
    enabled: debounced != null,
  });
  const ents = data?.entities || [];

  return (
    <div className="rounded-md border border-[#2D3442] bg-[#13171F] p-6" data-testid="time-scrubber">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div><div className="lf-label mb-1">Snapshot</div><h3 className="font-display text-3xl">Where is everyone?</h3></div>
        <div className="flex items-center gap-2 text-sm text-[#A8A090]">
          {mapLink && <Link to={mapLink} className="mr-2 inline-flex items-center gap-1 text-xs text-[#F59E0B] hover:underline" data-testid="scrubber-view-on-map"><MapIcon className="h-3.5 w-3.5" />View on map</Link>}
          at
          <Input type="number" className="w-28 h-9 font-mono-lf" value={value} onChange={(e) => onChange(parseInt(e.target.value || "0", 10))} data-testid="scrubber-value-input" />
          <span className="whitespace-nowrap text-[#F3EBDD]" data-testid="scrubber-date-label">{fmtT(fmt, value)}</span>
        </div>
      </div>
      <Slider min={min} max={max} step={1} value={[Math.min(max, Math.max(min, value))]} onValueChange={([v]) => onChange(v)} data-testid="scrubber-slider" />
      <div className="mt-1.5 flex justify-between text-[10px] font-mono-lf text-[#A8A090]"><span>{fmtT(fmt, min, true)}</span><span>{fmtT(fmt, max, true)}</span></div>
      {data?.events_at_moment?.length > 0 && (
        <div className="mt-4 text-sm text-[#F59E0B]" data-testid="scrubber-moment-events">This moment: {data.events_at_moment.map((e) => e.title).join(" · ")}</div>
      )}
      <div className="mt-6 grid gap-2 md:grid-cols-2" data-testid="scrubber-results">
        {data?.offline && <p className="text-sm text-[#FBBF24]" data-testid="scrubber-offline">Snapshots need an internet connection.</p>}
        {ents.length === 0 && !data?.offline && <p className="text-sm text-[#A8A090]">No characters or factions in the Lore Bible yet.</p>}
        {ents.map((c) => (
          <div key={c.entity_id} className={`rounded border border-[#2D3442] px-4 py-3 ${c.latest_event ? "bg-[#1A202C]" : "opacity-50"}`} data-testid={`snapshot-row-${c.entity_id}`}>
            <button className="font-display text-xl hover:text-[#F59E0B]" style={{ color: catColor(c.category) }} onClick={() => onOpenEntity(c.entity_id)}>{c.name}</button>
            <div className="mt-1 flex items-center gap-1.5 text-sm text-[#E8DFD1]"><MapPin className="h-3.5 w-3.5 text-[#10B981]" />
              {c.away ? <span className="text-[#C4B5FD]" data-testid={`snapshot-away-${c.entity_id}`}>Away in {c.away.timeline_name}{c.away.event_title ? ` · ${c.away.event_title}` : ""}</span>
                : c.location ? <button className="hover:text-[#F59E0B]" onClick={() => onOpenEntity(c.location.id)} data-testid={`snapshot-location-${c.entity_id}`}>{c.location.name}</button> : <span className="text-[#A8A090]">Whereabouts unknown</span>}
              {c.time_travelled && <span className="ml-1 inline-flex items-center rounded-full border border-[#8B5CF6]/60 px-1.5 text-[9px] uppercase tracking-wider text-[#C4B5FD]" title="This character has time-travelled" data-testid={`snapshot-tt-${c.entity_id}`}>⧗ time-travelled</span>}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-[#A8A090]"><Clock className="h-3 w-3" />{c.latest_event ? `${c.latest_event.title} · ${evDate(c.latest_event)}` : "Not yet appeared"}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
